import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";

const CATEGORIES = [
  {
    slug: "wardrobe",
    name: "The Wardrobe",
    category: "Apparel",
    subtitle: "Apparel · Outerwear · Couture",
    description:
      "Sculptural denim and silk silhouettes — hand-finished in our Milanese atelier.",
    image:
      "https://images.unsplash.com/photo-1697924293303-34488b60bf36?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNDR8MHwxfHNlYXJjaHwxfHxoaWdoJTIwZmFzaGlvbiUyMG1vZGVsJTIwZGFya3xlbnwwfHx8fDE3ODExMDk0NTl8MA&ixlib=rb-4.1.0&q=85",
    accent: "from-denim/30",
  },
  {
    slug: "finishing-touch",
    name: "The Finishing Touch",
    category: "Jewellery",
    subtitle: "Jewellery · Hardware",
    description:
      "Sterling silver forged into halos, chains, and quiet shimmer for the everyday.",
    image:
      "https://images.unsplash.com/photo-1606760227091-3dd870d97f1d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNTl8MHwxfHNlYXJjaHwxfHxsdXh1cnklMjBoYW5kYmFnJTIwamV3ZWxyeXxlbnwwfHx8fDE3ODExMDk0NTl8MA&ixlib=rb-4.1.0&q=85",
    accent: "from-silver/20",
  },
  {
    slug: "carry-edit",
    name: "The Carry Edit",
    category: "Handbag",
    subtitle: "Handbags · Leather Goods",
    description:
      "Architectural carries cut from Tuscan calfskin — built to outlast the season.",
    image:
      "https://images.unsplash.com/photo-1590739169125-a9438401596a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNTl8MHwxfHNlYXJjaHw0fHxsdXh1cnklMjBoYW5kYmFnJTIwamV3ZWxyeXxlbnwwfHx8fDE3ODExMDk0NTl8MA&ixlib=rb-4.1.0&q=85",
    accent: "from-denim-light/25",
  },
  {
    slug: "grounded-edit",
    name: "The Grounded Edit",
    category: "Footwear",
    subtitle: "Footwear · Heels · Boots",
    description:
      "Precision-cut heels and studded boots — sculpted to catch the light.",
    image:
      "https://images.unsplash.com/photo-1769787147452-921f573829bd?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1Mjh8MHwxfHNlYXJjaHwzfHxsdXh1cnklMjBkZXNpZ25lciUyMHNob2VzfGVufDB8fHx8MTc4MTEwOTQ3MXww&ixlib=rb-4.1.0&q=85",
    accent: "from-silver/15",
  },
];

export default function CategoryShowcase({ onSelect }) {
  return (
    <section
      id="categories"
      data-testid="category-showcase"
      className="relative bg-midnight pt-20 md:pt-28 pb-10 md:pb-16 px-6 md:px-12 lg:px-24"
    >
      {/* Header */}
      <div className="grid grid-cols-1 md:grid-cols-12 gap-8 mb-12 md:mb-16">
        <div className="md:col-span-4">
          <div className="eyebrow mb-4">Four Edits</div>
        </div>
        <div className="md:col-span-8">
          <motion.h2
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.4 }}
            transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
            className="font-serif text-white text-4xl sm:text-5xl md:text-6xl leading-[0.95] tracking-tight"
          >
            Enter the <span className="italic text-silver">house</span>
            <br />
            through any door.
          </motion.h2>
        </div>
      </div>

      {/* 2x2 editorial grid on desktop, stacked on mobile */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
        {CATEGORIES.map((c, i) => (
          <motion.button
            key={c.slug}
            type="button"
            onClick={() => onSelect && onSelect(c.category)}
            data-testid={`category-card-${c.slug}`}
            initial={{ opacity: 0, y: 36 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{
              duration: 1,
              delay: i * 0.08,
              ease: [0.22, 1, 0.36, 1],
            }}
            className="group relative block overflow-hidden bg-midnight-panel aspect-[4/5] md:aspect-[3/4] text-left w-full"
          >
            {/* Image */}
            <div className="absolute inset-0 overflow-hidden">
              <img
                src={c.image}
                alt={c.name}
                loading="lazy"
                className="product-img absolute inset-0 w-full h-full object-cover scale-105"
              />
              {/* Gradient overlays */}
              <div className="absolute inset-0 bg-gradient-to-t from-[#0a0b10] via-[#0a0b10]/30 to-transparent" />
              <div
                className={`absolute inset-0 bg-gradient-to-tr ${c.accent} via-transparent to-transparent opacity-70 group-hover:opacity-100 transition-opacity duration-700`}
              />
            </div>

            {/* Top meta */}
            <div className="absolute top-6 left-6 right-6 flex items-start justify-between text-[10px] tracking-[0.32em] uppercase">
              <span className="text-silver">Edit · 0{i + 1}</span>
              <span className="text-white/70">{c.subtitle}</span>
            </div>

            {/* Bottom content */}
            <div className="absolute bottom-0 left-0 right-0 p-7 md:p-10">
              <motion.div
                className="overflow-hidden"
                initial={false}
              >
                <h3 className="font-serif text-white text-3xl md:text-4xl lg:text-5xl leading-tight tracking-tight">
                  {c.name}
                </h3>
              </motion.div>

              {/* Description — slides up on hover */}
              <div
                className="mt-3 max-h-0 opacity-0 group-hover:max-h-24 group-hover:opacity-100 group-hover:mt-4 transition-all duration-700 ease-out overflow-hidden"
                data-testid={`category-description-${c.slug}`}
              >
                <p className="text-white/70 text-sm md:text-base font-light leading-relaxed max-w-md">
                  {c.description}
                </p>
              </div>

              <div className="mt-6 flex items-center justify-between border-t border-white/15 pt-5">
                <span className="text-[10px] tracking-[0.4em] uppercase text-white/70 group-hover:text-white transition-colors">
                  Explore the edit
                </span>
                <span className="w-10 h-10 border border-white/25 flex items-center justify-center text-white/80 group-hover:border-white group-hover:text-white group-hover:bg-white/5 group-hover:rotate-[-45deg] transition-all duration-500">
                  <ArrowUpRight size={14} strokeWidth={1.3} />
                </span>
              </div>
            </div>
          </motion.button>
        ))}
      </div>
    </section>
  );
}
